import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Mic, Square, Loader2, X, Check, MapPin, AlertCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * LiveSiteVisitDialog — Flow C "field journaling".
 *
 * Rep is at the site on their phone. Hits Start. Records short voice clips
 * back-to-back; each clip is transcribed and shown in a live feed. Hits End
 * to finalize: visit becomes `completed` but `debrief_completed` stays false
 * so the rep can run the structured Voice Debrief on it later.
 */
export default function LiveSiteVisitDialog({ open, onOpenChange, leadId, onSaved = () => {} }) {
  const [phase, setPhase] = useState("idle"); // idle | recording | uploading | between | finalising
  const [visitId, setVisitId] = useState(null);
  const [clips, setClips] = useState([]); // [{ interaction_id, transcript, clip_index }]
  const [seconds, setSeconds] = useState(0);
  const [summaryNote, setSummaryNote] = useState("");
  const [errorMsg, setErrorMsg] = useState(null);

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  // Reset on open / close
  useEffect(() => {
    if (open) {
      setPhase("idle");
      setVisitId(null);
      setClips([]);
      setSeconds(0);
      setSummaryNote("");
      setErrorMsg(null);
    } else {
      cleanupAudio();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const cleanupAudio = () => {
    try { mediaRecorderRef.current?.stop(); } catch {}
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    streamRef.current = null;
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  // Start the live visit session (creates the in_progress visit doc)
  const startSession = async () => {
    try {
      const res = await axios.post(`${API}/voice/site-visit/start`, { lead_id: leadId });
      setVisitId(res.data.visit_id);
      if (res.data.resumed) toast.info("Resuming an in-progress visit");
      // If clips already exist on a resumed session, fetch them
      if (res.data.resumed) {
        try {
          const cl = await axios.get(`${API}/voice/site-visit/${res.data.visit_id}/clips`);
          setClips((cl.data || []).map((c, i) => ({
            interaction_id: c.interaction_id,
            transcript: c.transcript || c.content || "",
            clip_index: i + 1,
          })));
        } catch {}
      }
      setPhase("between");
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || "Could not start live visit. Try again.");
    }
  };

  // Pick a mimeType the current browser actually supports (iOS Safari ≠ Chrome)
  const pickSupportedMime = () => {
    const candidates = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/mp4;codecs=mp4a.40.2",
      "audio/mp4",
      "audio/ogg;codecs=opus",
    ];
    if (typeof MediaRecorder === "undefined") return "";
    for (const m of candidates) {
      try { if (MediaRecorder.isTypeSupported(m)) return m; } catch {}
    }
    return "";
  };

  // Begin recording one clip
  const recordClip = async () => {
    setErrorMsg(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickSupportedMime();
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        chunksRef.current = [];
        // Guard: don't upload a clip if the dialog/visit was discarded mid-recording
        if (!visitId) return;
        await uploadClip(blob);
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setPhase("recording");
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (err) {
      setErrorMsg(err?.message || "Microphone permission denied or unavailable.");
    }
  };

  const stopRecording = () => {
    try { mediaRecorderRef.current?.stop(); } catch {}
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    setPhase("uploading");
  };

  const uploadClip = async (blob) => {
    if (!visitId) return;
    try {
      const fd = new FormData();
      fd.append("audio", blob, "clip.webm");
      const res = await axios.post(`${API}/voice/site-visit/${visitId}/clip`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setClips((prev) => [...prev, {
        interaction_id: res.data.interaction_id,
        transcript: res.data.transcript,
        clip_index: res.data.clip_index,
      }]);
      setSeconds(0);
      setPhase("between");
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || "Clip upload failed. Try again.");
      setPhase("between");
    }
  };

  // Finalize — visit completes, debrief stays unconfirmed
  const finishVisit = async () => {
    if (!visitId) return;
    setPhase("finalising");
    try {
      const res = await axios.post(`${API}/voice/site-visit/${visitId}/end`, {
        summary_note: summaryNote.trim() || null,
      });
      toast.success(`Visit logged · ${res.data.clip_count} clip${res.data.clip_count === 1 ? "" : "s"}. Debrief pending.`);
      onSaved(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to finalize visit");
      setPhase("between");
    }
  };

  const cancelVisit = async () => {
    if (!visitId) {
      onOpenChange(false);
      return;
    }
    if (!window.confirm("Discard this live visit and all recorded clips?")) return;
    try {
      await axios.post(`${API}/voice/site-visit/${visitId}/end`, { cancel: true });
      toast.info("Visit discarded");
    } catch {
      toast.error("Failed to discard");
    } finally {
      onOpenChange(false);
    }
  };

  const renderTimer = () => {
    const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
    const ss = String(seconds % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="live-visit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-rose-600" />
            Live Site Visit
            {phase === "recording" && (
              <Badge className="bg-rose-100 text-rose-700 border-rose-200 ml-1">
                <span className="w-1.5 h-1.5 rounded-full bg-rose-600 animate-pulse mr-1" />
                LIVE
              </Badge>
            )}
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-1">
            Record short clips while you're at the site. Each clip is transcribed and added to the lead's history.
          </p>
        </DialogHeader>

        {/* Idle — start prompt */}
        {phase === "idle" && (
          <div className="flex flex-col items-center py-6 space-y-3" data-testid="live-visit-idle">
            <div className="w-20 h-20 rounded-full bg-rose-100 flex items-center justify-center">
              <MapPin className="w-9 h-9 text-rose-600" />
            </div>
            <p className="text-sm text-slate-700 text-center max-w-sm">
              Walk through the site and record observations as you go. End anytime — the visit will be marked
              <span className="font-semibold"> completed</span> with the debrief left for you to wrap up later.
            </p>
            <Button onClick={startSession} className="bg-rose-600 hover:bg-rose-700 mt-2" data-testid="start-live-visit-btn">
              <MapPin className="w-4 h-4 mr-1" /> Start live visit
            </Button>
          </div>
        )}

        {/* Active session: between or recording */}
        {(phase === "recording" || phase === "between" || phase === "uploading") && visitId && (
          <div className="space-y-4" data-testid="live-visit-active">
            {/* Recorder strip */}
            <div className={`rounded-xl border p-4 flex items-center justify-between ${phase === "recording" ? "bg-rose-50 border-rose-200" : "bg-slate-50 border-slate-200"}`}>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                  {phase === "recording" ? "Recording clip" : phase === "uploading" ? "Transcribing…" : `Clip ${clips.length + 1}`}
                </p>
                <p className="font-mono text-2xl font-semibold text-slate-700">{renderTimer()}</p>
              </div>
              {phase === "recording" ? (
                <Button onClick={stopRecording} variant="destructive" data-testid="stop-clip-btn">
                  <Square className="w-4 h-4 mr-1" /> Stop
                </Button>
              ) : phase === "uploading" ? (
                <Loader2 className="w-6 h-6 text-violet-600 animate-spin" />
              ) : (
                <Button onClick={recordClip} className="bg-rose-600 hover:bg-rose-700" data-testid="record-clip-btn">
                  <Mic className="w-4 h-4 mr-1" /> Record clip
                </Button>
              )}
            </div>

            {/* Clip feed */}
            <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="live-clip-feed">
              {clips.length === 0 && (
                <p className="text-xs text-slate-400 text-center py-4">No clips yet — tap Record to capture your first observation.</p>
              )}
              {clips.map((c) => (
                <div key={c.interaction_id} className="bg-white border border-slate-200 rounded-lg p-3" data-testid={`clip-row-${c.clip_index}`}>
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-violet-600 font-semibold mb-1">
                    <Check className="w-3 h-3" /> Clip {c.clip_index} · transcribed
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed">{c.transcript}</p>
                </div>
              ))}
            </div>

            {/* Wrap-up note + actions */}
            {phase === "between" && (
              <div className="space-y-2 pt-2 border-t border-slate-100">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Wrap-up note (optional)</p>
                <Textarea
                  rows={2}
                  placeholder="A one-liner that should appear on the visit feedback (e.g. 'Liked the master bedroom; concerned about parking')"
                  value={summaryNote}
                  onChange={(e) => setSummaryNote(e.target.value)}
                  data-testid="wrapup-note-input"
                />
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              <Button variant="ghost" size="sm" onClick={cancelVisit} data-testid="cancel-live-visit-btn" disabled={phase !== "between"}>
                <X className="w-4 h-4 mr-1" /> Discard visit
              </Button>
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700"
                disabled={phase !== "between" || clips.length === 0}
                onClick={finishVisit}
                data-testid="end-live-visit-btn"
              >
                {phase === "finalising" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                End visit
              </Button>
            </div>
          </div>
        )}

        {/* Finalising spinner */}
        {phase === "finalising" && (
          <div className="flex flex-col items-center py-8 space-y-2">
            <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
            <p className="text-sm text-slate-700">Wrapping up the visit…</p>
          </div>
        )}

        {/* Error */}
        {errorMsg && (
          <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 flex items-start gap-2 text-xs text-rose-700" data-testid="live-visit-error">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{errorMsg}</span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
